import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { get } from '../../utils/api';
import { getKotilingaCategorySlugsCsv } from '../../utils/kotilingaScope';

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Pending Pledges Dialog - Dashboard only (always shows pending pledges)
export function PendingPledgesDialog({ open, onClose, product, buildRangeParams, useParentCategorySlugs, useKotilingaFlag }) {
  const [rows, setRows] = React.useState([]);
  const [expandedCustomers, setExpandedCustomers] = React.useState({});
  const [customerTransactions, setCustomerTransactions] = React.useState({});
  const [loadingTransactions, setLoadingTransactions] = React.useState({});

  // Load pledges when dialog opens or product changes
  React.useEffect(() => {
    if (!open || !product) {
      setRows([]);
      setExpandedCustomers({});
      setCustomerTransactions({});
      setLoadingTransactions({});
      return;
    }

    const loadPledges = async () => {
      try {
        const pid = product.product_id;
        const vid = product.variation?.variation_id;
        
        const useFlag = useKotilingaFlag === true || useKotilingaFlag === false;
        const range = useFlag ? '' : buildRangeParams();
        const scopeSlugs = getKotilingaCategorySlugsCsv();
        const categoryParam = useParentCategorySlugs
          ? `parent_category_slugs=${encodeURIComponent(useParentCategorySlugs)}`
          : `exclude_parent_category_slugs=${encodeURIComponent(scopeSlugs)}`;
        const pledgeParams = new URLSearchParams();
        pledgeParams.append('status', 'pending');
        if (useFlag) {
          pledgeParams.append(useKotilingaFlag ? 'kotilinga' : 'temple', 'true');
        } else {
          pledgeParams.append(
            useParentCategorySlugs ? 'parent_category_slugs' : 'exclude_parent_category_slugs',
            useParentCategorySlugs ? useParentCategorySlugs : scopeSlugs
          );
          if (range) {
            const startMatch = range.match(/start=([^&]+)/);
            const endMatch = range.match(/end=([^&]+)/);
            if (startMatch?.[1]) pledgeParams.append('start', decodeURIComponent(startMatch[1]));
            if (endMatch?.[1]) pledgeParams.append('end', decodeURIComponent(endMatch[1]));
          }
        }
        
        if (product.variation && vid !== null && vid !== undefined) {
          // Use the backend endpoint for variation-specific pledges (always pending for dashboard)
          const res = await get(`/ldb/reports/pledges/by-product/${pid}/variations/${vid}/customers?${pledgeParams.toString()}`);
          const data = Array.isArray(res?.data) ? res.data : [];
          setRows(data);
        } else {
          // For product-level, would need to fetch differently
          // For now, set empty - can be enhanced later
          setRows([]);
        }
      } catch (e) {
        console.error('[PendingPledgesDialog] Failed to load pledges:', e);
        setRows([]);
      }
    };

    loadPledges();
  }, [open, product, buildRangeParams]);

  const toggleCustomerTransactions = async (customerKey, customer) => {
    const isExpanded = !!expandedCustomers[customerKey];
    if (isExpanded) {
      setExpandedCustomers(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
      return;
    }

    setExpandedCustomers(prev => ({ ...prev, [customerKey]: true }));
    
    if (customerTransactions[customerKey]) {
      return;
    }

    setLoadingTransactions(prev => ({ ...prev, [customerKey]: true }));
    
    try {
      const pid = product?.product_id;
      const vid = product?.variation?.variation_id;
      
      if (!pid) {
        setLoadingTransactions(prev => {
          const next = { ...prev };
          delete next[customerKey];
          return next;
        });
        return;
      }

      const useFlag = useKotilingaFlag === true || useKotilingaFlag === false;
      const range = useFlag ? '' : buildRangeParams();
      const scopeSlugs = getKotilingaCategorySlugsCsv();
      const categoryParam = useParentCategorySlugs
        ? `parent_category_slugs=${encodeURIComponent(useParentCategorySlugs)}`
        : `exclude_parent_category_slugs=${encodeURIComponent(scopeSlugs)}`;
      
      let allOrders = [];
      let pageNum = 1;
      const perPageNum = 100;
      
      while (true) {
        const queryParams = [
          `status=completed`,
          `product_id=${encodeURIComponent(pid)}`,
          `per_page=${perPageNum}`,
          `page=${pageNum}`,
          categoryParam
        ];
        
        if (customer.customer_id) {
          queryParams.push(`customer_id=${encodeURIComponent(customer.customer_id)}`);
        }
        if (customer.email) {
          queryParams.push(`email=${encodeURIComponent(customer.email)}`);
        }
        
        if (range) {
          queryParams.push(range);
        }
        
        const res = await get(`/ldb/orders/search?${queryParams.join('&')}`);
        const batch = Array.isArray(res?.data) ? res.data : [];
        
        const enriched = await Promise.all(batch.map(async (o) => {
          try {
            const det = await get(`/ldb/orders/${o.id}`);
            const orderData = det?.order || det || {};
            return { 
              ...o, 
              ...orderData,
              items: Array.isArray(det?.items) ? det.items : [],
              payment_method: orderData.payment_method || o.payment_method || '',
              payment_method_title: orderData.payment_method_title || o.payment_method_title || ''
            };
          } catch {
            return { ...o, items: [] };
          }
        }));
        
        const donorKey = (r) => {
          const em = String(r.billing_email || '').trim().toLowerCase();
          if (em && em !== '') return em;
          if (r.customer_id) return `cust#${r.customer_id}`;
          return `order#${r.id || 0}`;
        };
        
        const filtered = enriched.filter(o => {
          const orderKey = donorKey(o);
          if (orderKey !== customerKey) return false;
          
          const items = o.items || o.line_items || [];
          return items.some(item => {
            const itemPid = item.product_id || item.productId || '';
            if (String(itemPid) !== String(pid)) return false;
            if (vid !== null && vid !== undefined) {
              const itemVid = item.variation_id || item.variationId || 0;
              return String(itemVid || 0) === String(vid);
            }
            return true;
          });
        });
        
        allOrders = allOrders.concat(filtered);
        
        if (batch.length < perPageNum) break;
        pageNum += 1;
        if (pageNum > 50) break;
      }
      
      const transactions = allOrders.flatMap(o => {
        const items = o.items || o.line_items || [];
        return items
          .filter(item => {
            const itemPid = item.product_id || item.productId || '';
            if (String(itemPid) !== String(pid)) return false;
            if (vid !== null && vid !== undefined) {
              const itemVid = item.variation_id || item.variationId || 0;
              return String(itemVid || 0) === String(vid);
            }
            return true;
          })
          .map(item => ({
            orderId: o.id,
            orderNumber: o.order_number || null,
            date: o.date_created_gmt || o.date_created,
            status: o.status,
            amount: Number(item.total_num || item.total || item.subtotal || 0),
            quantity: Number(item.quantity || 0),
            paymentMethod: o.payment_method_title || o.payment_method || ''
          }));
      }).sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setCustomerTransactions(prev => ({ ...prev, [customerKey]: transactions }));
    } catch (e) {
      console.error('[PendingPledgesDialog] Failed to load customer transactions:', e);
      setCustomerTransactions(prev => ({ ...prev, [customerKey]: [] }));
    } finally {
      setLoadingTransactions(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
    }
  };

  // Calculate totals for summary row
  const totals = rows.reduce((acc, r) => ({
    pledge_total: acc.pledge_total + Number(r.pledge_total || 0),
    paid_total: acc.paid_total + Number(r.paid_total || 0),
    balance_total: acc.balance_total + Number(r.balance_total || 0),
    count: acc.count + 1
  }), { pledge_total: 0, paid_total: 0, balance_total: 0, count: 0 });

  const title = product
    ? (product.variation
        ? `${product.product_name || `#${product.product_id}`} — ${product.variation.variation_name || `Variation #${product.variation.variation_id}`}`
        : (product.product_name || `#${product.product_id}`))
    : 'Pending Pledges';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {title} — Devotees
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: '600px', maxHeight: '80vh', overflow: 'auto' }}>
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(200px,1fr) 140px 140px 140px', bgcolor: 'action.hover' }}>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700 }}>Devotee</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Pledge</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Paid</Typography>
            <Typography variant="caption" sx={{ p: 1, fontWeight: 700, textAlign: 'right' }}>Balance</Typography>
          </Box>
          {/* Totals row */}
          {rows.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(200px,1fr) 140px 140px 140px', borderTop: '2px solid', borderColor: 'primary.main', bgcolor: 'primary.light', '& *': { color: 'primary.contrastText', fontWeight: 700 } }}>
              <Typography variant="body2" sx={{ p: 1 }}>
                Total ({totals.count} {totals.count === 1 ? 'devotee' : 'devotees'})
              </Typography>
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.pledge_total)}</Typography>
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.paid_total)}</Typography>
              <Typography variant="body2" sx={{ p: 1, textAlign: 'right' }}>{formatAmount(totals.balance_total)}</Typography>
            </Box>
          )}
          <Box>
            {rows.map((r, i) => {
              const email = String(r.email || '').trim().toLowerCase();
              const customerKey = email ? email : (r.customer_id ? `cust#${r.customer_id}` : `order#${r.customer_id || 0}`);
              const isExpanded = !!expandedCustomers[customerKey];
              const transactions = customerTransactions[customerKey] || [];
              const isLoading = !!loadingTransactions[customerKey];
              
              return (
                <React.Fragment key={`${r.customer_id}-${i}`}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(200px,1fr) 140px 140px 140px',
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    onClick={() => toggleCustomerTransactions(customerKey, r)}
                  >
                    <Typography variant="body2" sx={{ p: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      <span style={{ color: '#1976d2', fontWeight: 500 }}>
                        {r.name || r.email || `#${r.customer_id}`}
                      </span>
                    </Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right' }}>{formatAmount(r.pledge_total)}</Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right' }}>{formatAmount(r.paid_total)}</Typography>
                    <Typography variant="body2" sx={{ p: 0.5, textAlign: 'right', fontWeight: 600 }}>{formatAmount(r.balance_total)}</Typography>
                  </Box>
                  {isExpanded && (
                    <Box sx={{ pl: 3, pr: 1, pb: 1, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
                      {isLoading ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Loading transactions...</Typography>
                      ) : transactions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No transactions found</Typography>
                      ) : (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>Transactions:</Typography>
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px 120px', bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Date</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Order #</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700 }}>Payment Method</Typography>
                              <Typography variant="caption" sx={{ p: 0.5, fontWeight: 700, textAlign: 'right' }}>Amount</Typography>
                            </Box>
                            {transactions.map((txn, txnIdx) => (
                              <Box key={txnIdx} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 140px 120px', borderTop: txnIdx > 0 ? '1px solid' : 'none', borderColor: 'divider', bgcolor: txnIdx % 2 === 0 ? 'transparent' : 'action.hover' }}>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  {txn.date ? new Date(txn.date).toLocaleDateString() : 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  {txn.orderNumber || `#${txn.orderId}`}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem' }}>
                                  {txn.paymentMethod || 'N/A'}
                                </Typography>
                                <Typography variant="body2" sx={{ p: 0.5, fontSize: '0.75rem', textAlign: 'right', fontWeight: 500 }}>
                                  {formatAmount(txn.amount)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>No data</Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

